import { useEffect, useState } from 'react';
import ErrorUtils from "src/utils/ErrorUtils";
import { parseExcelFromArrayBuffer, ExcelRowConverter } from "src/utils/ExcelUtils";

/**
 * MDA Results row data with simplified array-based structure
 */
export interface MDAResultsRow {
    row: (string | number | undefined)[];
}

export interface ExcelHeaderData {
    value: string;
}

/**
 * Simplified converter for MDA results data
 */
const mdaResultsConverter: ExcelRowConverter<MDAResultsRow> = (rowData: any[], headers: string[], rowIndex: number): MDAResultsRow => {
    // Convert row data to simplified array format
    const row: (string | number | undefined)[] = headers.map((header, columnIndex) => {
        const cellValue = rowData[columnIndex];
        
        // Handle different data types
        if (cellValue === null || cellValue === undefined) {
            return undefined;
        }
        
        return cellValue;
    });

    return {
        row
    };
};

export interface MDAResultsData {
    excelData: MDAResultsRow[];
    headers: ExcelHeaderData[];
    loading: boolean;
    error: string;
}

// Shared empty values keep the same identity on every render, so consumers' memos and effects stay quiet.
const NO_ROWS: MDAResultsRow[] = [];
const NO_HEADERS: ExcelHeaderData[] = [];
export const NO_RESULT_FILE: MDAResultsData = { excelData: NO_ROWS, headers: NO_HEADERS, loading: false, error: '' };
const RESULT_FILE_LOADING: MDAResultsData = { excelData: NO_ROWS, headers: NO_HEADERS, loading: true, error: '' };

/**
 * MDA Results-specific Excel data hook with simplified data structure
 *
 * @param preSignedUrl - The pre-signed URL to fetch the Excel file from
 * @returns Object containing excel data, headers, loading state, and error message
 */
export const useMDAResultsData = (preSignedUrl?: string): MDAResultsData => {
    const [loaded, setLoaded] = useState<{ url?: string, file: MDAResultsData }>({ file: NO_RESULT_FILE });

    useEffect(() => {
        if (!preSignedUrl) {
            return;
        }

        // A slower response for an earlier URL must not replace the file of the URL now requested.
        let active = true;
        const fetchAndParseExcel = async () => {
            let file: MDAResultsData;
            try {
                // Fetch the Excel file from the preSignedUrl
                const response = await fetch(preSignedUrl);
                if (!response.ok) {
                    throw new Error(`Failed to load excel sheet, received http: ${response.status}`);
                }

                // Get the file as array buffer
                const arrayBuffer = await response.arrayBuffer();

                // Parse Excel using MDA Results-specific converter
                const parseResult = parseExcelFromArrayBuffer<MDAResultsRow>(
                    arrayBuffer,
                    mdaResultsConverter,
                    {   // parser options:
                        defaultValue: "",
                        sheetIndex: 1
                    }
                );

                // Transform headers to simple format (no validation needed)
                const headerData: ExcelHeaderData[] = parseResult.headers.map(headerValue => ({ value: headerValue }));
                file = headerData.length === 0
                    ? NO_RESULT_FILE
                    : { excelData: parseResult.data, headers: headerData, loading: false, error: '' };
            } catch (err) {
                file = { ...NO_RESULT_FILE, error: ErrorUtils.getMessage(err) };
            }
            if (active) {
                setLoaded({ url: preSignedUrl, file });
            }
        };

        fetchAndParseExcel();
        return () => {
            active = false;
        };
    }, [preSignedUrl]);

    // Rows belong to the URL they were read from, so a new URL reads as loading until its own file lands.
    if (!preSignedUrl) {
        return NO_RESULT_FILE;
    }
    return loaded.url === preSignedUrl ? loaded.file : RESULT_FILE_LOADING;
};
